import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Bot,
  Check,
  ChevronUp,
  Circle,
  Hand,
  Hash,
  LayoutGrid,
  Link2,
  MessageSquare,
  MonitorOff,
  MonitorUp,
  PanelRight,
  PictureInPicture,
  PictureInPicture2,
  Settings,
  Share2,
  Smile,
  Square,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallDuration } from "../hooks/useCallDuration";
import { useCallLayoutTier } from "../hooks/useCallLayoutTier";
import { useCallPip } from "../lib/callPipContext";
import type { UseAudioOutputControlResult } from "../hooks/useAudioOutputControl";
import type { UseCallDevicesResult } from "../hooks/useCallDevices";
import type { MediaErrorCause } from "../lib/deviceErrors";
import { DeviceAffordance } from "./DeviceAffordance";
import { InCallCameraButton, InCallMicButton } from "./InCallDeviceButtons";
import { HangUpIcon } from "./HangUpIcon";
import { MeetingControlsOverflow } from "./MeetingControlsOverflow";
import { InCallShareDialog } from "./InCallShareDialog";
import { RECORDING_ENABLED } from "../lib/featureFlags";
import type { RingStatusPill } from "../hooks/useOutgoingRings";

export type SidePanelTab = "chat" | "users" | "invite" | null;
export type ViewMode = "grid" | "speaker" | "sidebar";

interface MeetingControlsProps {
  /** Display only — call metadata for the left meta block. */
  roomName: string;
  /** Short human-readable join code shown in the meeting-info popover
   *  (Room.join_code) — null/undefined hides that row once loaded. */
  meetingCode?: string | null;
  /** True until the room details arrive — the code row shows a loading
   *  placeholder instead of silently disappearing (GH-1768). */
  meetingCodeLoading?: boolean;
  /** The meeting's share link — same URL as "Create a link to share"
   *  (`room.join_url ?? {origin}/call/{id}`). Shown and copied verbatim. */
  shareLink: string;
  /** Backend room UUID — enables the Share button, whose "Invite people"
   *  card invites through `POST /rooms/{id}/invite/`. Omit to hide it. */
  roomId?: string;
  /** Total participant count (including local). The participants badge shows
   *  this full total (incl. the local user); the header subtitle shows N-1 "others". */
  participantCount: number;
  /** Guests currently in the waiting room — shown as a small badge on the
   *  People button itself (matches the design's `waiting.length > 0` badge,
   *  visible even before the panel opens), in addition to the "Waiting
   *  room" section already rendered inside the People side panel. */
  waitingCount?: number;
  /** Epoch ms when the local user joined. Drives the live duration ticker. */
  joinedAt: number;

  micOn: boolean;
  onToggleMic: () => void;
  /** Non-null when the local mic can't be acquired (GH-1179) — renders a
   *  persistent inline affordance above the controls, by the mic toggle. */
  micError?: MediaErrorCause | null;
  /** The call's mic / speaker / camera lists and selection (GH-1859) — the
   *  mic arrow's merged menu and the camera arrow read and switch these. */
  callDevices: UseCallDevicesResult;
  cameraOn: boolean;
  onToggleCamera: () => void;
  screenSharing: boolean;
  onToggleScreenShare: () => void;
  /** Disables the screen-share button when another participant holds the slot. */
  screenShareDisabled?: boolean;
  /** True on Android/iOS — screen share is unavailable on mobile web browsers. */
  screenShareNotSupported?: boolean;
  /** #215 — true when the local session role is "guest": guests hold no
   *  screen_share publish grant server-side, so the button is disabled with
   *  a role-specific explanation rather than the generic "busy" label. */
  screenShareGuestBlocked?: boolean;

  handRaised: boolean;
  onToggleHandRaised: () => void;

  /** Visual-only "Record meeting" toggle (no real recording backend). */
  recording: boolean;
  onToggleRecording: () => void;

  /** Currently open side panel (or null if closed) — drives the shared
   *  SidePanel (rendered by the caller) for chat and people on every tier,
   *  matching the design's full-height slide-in treatment for both. Settings
   *  is the one exception: it stays an anchored popover on desktop (see
   *  inviteContent), matching the design's own small "Room settings" dropdown. */
  activePanel: SidePanelTab;
  onPanelChange: (next: SidePanelTab) => void;
  /** Unread in-call chat messages — shown as a numeric badge on the chat
   *  button while the chat side-panel is closed. Caller resets on open. */
  chatUnreadCount?: number;
  /** Whether the local user has the "admin" session role (#215) — controls
   *  settings button visibility. Historically room-ownership-only; now
   *  role-aware so a promoted admin also sees it and a demoted one loses it. */
  isHost?: boolean;
  /** Rendered inside the desktop Settings/Invite popover (host-only). */
  inviteContent: React.ReactNode;
  /** Black status pill overlaid on the control bar while a colleague is
   *  being rung / has accepted and is joining. */
  ringStatus?: RingStatusPill | null;

  /** Speaker (main-stage) vs grid layout. */
  viewMode: ViewMode;
  onViewModeChange: (next: ViewMode) => void;

  onLeave: () => void;
  onEmitReaction: (emoji: string) => void;
  /** Volume and the iPhone Earpiece / Loudspeaker route, shown in the mic
   *  menu's Speakers section. */
  audioOutput: UseAudioOutputControlResult;
  /** Dispatches Mochi (the assistant's meeting twin) into this call. Omitted
   *  hides the control entirely rather than rendering it disabled. */
  onSendMochi?: () => void;
}

const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "👏", "😮", "🙏"] as const;

/** Matches the design's own `[['grid', ...], ['speaker', ...], ['sidebar', ...]]`
 *  layout-menu list exactly (icon, label, description, in this order). */
const LAYOUT_OPTIONS: {
  mode: ViewMode;
  icon: typeof LayoutGrid;
  label: string;
  desc: string;
}[] = [
  { mode: "grid", icon: LayoutGrid, label: "call.controls.layoutGrid", desc: "call.controls.layoutGridDesc" },
  { mode: "speaker", icon: PictureInPicture2, label: "call.controls.layoutSpeaker", desc: "call.controls.layoutSpeakerDesc" },
  { mode: "sidebar", icon: PanelRight, label: "call.controls.layoutSidebar", desc: "call.controls.layoutSidebarDesc" },
];

function LayoutModeIcon({ mode, className }: { mode: ViewMode; className?: string }) {
  const Icon = mode === "grid" ? LayoutGrid : mode === "speaker" ? PictureInPicture2 : PanelRight;
  return <Icon className={className} />;
}

export function MeetingControls({
  roomName,
  meetingCode,
  meetingCodeLoading = false,
  shareLink,
  roomId,
  participantCount,
  waitingCount = 0,
  joinedAt,
  micOn,
  onToggleMic,
  micError,
  callDevices,
  cameraOn,
  onToggleCamera,
  screenSharing,
  onToggleScreenShare,
  screenShareDisabled = false,
  screenShareNotSupported = false,
  screenShareGuestBlocked = false,
  handRaised,
  onToggleHandRaised,
  recording,
  onToggleRecording,
  activePanel,
  onPanelChange,
  chatUnreadCount = 0,
  isHost = false,
  inviteContent,
  ringStatus = null,
  viewMode,
  onViewModeChange,
  onLeave,
  onEmitReaction,
  audioOutput,
  onSendMochi,
}: MeetingControlsProps) {
  const { t } = useTranslation();
  const duration = useCallDuration(joinedAt);

  const tier = useCallLayoutTier();
  const pip = useCallPip();
  const isDesktop = tier === "desktop";
  const isMobile = tier === "mobile";
  // Inline-visibility per tier. Desktop = everything inline (unchanged UI).
  const showScreenInline = !isMobile; // desktop + compact
  const showReactionsInline = isDesktop;
  const showInfoInline = !isMobile; // desktop + compact (GH-1768)
  const showPeopleInline = !isMobile; // desktop + compact
  const showChatInline = true;
  const showInviteInline = isDesktop;
  const showShareInline = !isMobile && Boolean(roomId); // desktop + compact
  const showLayoutsInline = isDesktop;
  const showHandRaiseInline = isDesktop;
  const showMore = !isDesktop;

  const togglePanel = (tab: NonNullable<SidePanelTab>) => {
    onPanelChange(activePanel === tab ? null : tab);
  };

  const [infoOpen, setInfoOpen] = useState(false);
  const infoWrapRef = useRef<HTMLDivElement | null>(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const reactionsWrapRef = useRef<HTMLDivElement | null>(null);
  const [layoutsOpen, setLayoutsOpen] = useState(false);
  const layoutsWrapRef = useRef<HTMLDivElement | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteWrapRef = useRef<HTMLDivElement | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useClickOutside(infoOpen, infoWrapRef, () => setInfoOpen(false));
  useClickOutside(reactionsOpen, reactionsWrapRef, () => setReactionsOpen(false));
  useClickOutside(layoutsOpen, layoutsWrapRef, () => setLayoutsOpen(false));
  useClickOutside(inviteOpen, inviteWrapRef, () => setInviteOpen(false));

  // Reset the "Copied!" confirmations a couple of seconds after they fire so
  // the buttons settle back to their idle label.
  useEffect(() => {
    if (!linkCopied) return;
    const id = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [linkCopied]);
  useEffect(() => {
    if (!codeCopied) return;
    const id = window.setTimeout(() => setCodeCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [codeCopied]);

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
    } catch {
      // Clipboard rejects on insecure contexts; the popover still shows the
      // URL so the user can copy manually.
    }
  };

  const handleCopyMeetingCode = async () => {
    if (!meetingCode) return;
    try {
      await navigator.clipboard.writeText(meetingCode);
      setCodeCopied(true);
    } catch {
      // no-op — see handleCopyShareLink
    }
  };

  const handlePickReaction = (emoji: string) => {
    onEmitReaction(emoji);
    setReactionsOpen(false);
  };

  const handlePickLayout = (mode: ViewMode) => {
    onViewModeChange(mode);
    setLayoutsOpen(false);
  };

  return (
    <div className="shrink-0">
      {/* Persistent mic affordance — GH-1179. Sits directly above the bar
          (which holds the mic toggle) so a listen-only / blocked-mic state
          reads as "by the mic control" instead of a vanishing toast. */}
      {micError && (
        <div className="flex justify-center py-2 bg-background border-t border-border">
          <DeviceAffordance kind="microphone" cause={micError} className="max-w-[90%] text-center" />
        </div>
      )}
      {/* On desktop the three columns keep the control cluster visually
          centered. On a phone that pattern is what made the timer slide
          over the mic — leftover width is too small, so the flex-1 meta
          column overflowed. Mobile is timer | shrinking controls | hangup. */}
      <div className={`relative flex items-center justify-between shrink-0 rounded-3xl bg-background border border-border ${isMobile ? "h-[4.25rem] mx-2 mb-3 px-2.5 gap-1.5" : "h-20 mx-3 mb-3 px-6"}`}>
        <div
          ref={infoWrapRef}
          data-call-bar-meta=""
          className={`flex items-center justify-start relative ${isMobile ? "shrink-0" : "flex-1 min-w-0"}`}
        >
        {/* No `overflow-hidden` here: the meeting-info popover is absolutely
            positioned above this wrapper and was clipped by it (GH-1768).
            The min-w-0 chain alone keeps the title truncating. */}
        {/* Compact timer — phones only. Hidden with `isMobile` (not `md:`)
            so a 641–767 compact bar doesn't also carry this block. */}
        {isMobile && (
          <button
            type="button"
            onClick={() => setInfoOpen((o) => !o)}
            title={t("call.controls.meetingInfo")}
            aria-label={t("call.controls.meetingInfo")}
            aria-expanded={infoOpen}
            className={`flex items-center gap-1.5 ps-2.5 pe-2 h-10 rounded-xl text-sm font-medium transition-colors ${
              infoOpen ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            }`}
          >
            {recording ? (
              <span className="size-2 rounded-full bg-red-500 anim-pulse-rec shrink-0" />
            ) : null}
            <span className="tabular-nums whitespace-nowrap">
              {duration}
            </span>
            <ChevronUp className={`size-[13px] shrink-0 transition-transform ${!infoOpen ? "rotate-180" : ""}`} />
          </button>
        )}

        {/* Meta block / meeting-info trigger — desktop + compact. */}
        {showInfoInline && (
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => setInfoOpen((o) => !o)}
              title={t("call.controls.meetingInfo")}
              aria-label={t("call.controls.meetingInfo")}
              aria-expanded={infoOpen}
              className={`flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-medium transition-colors max-w-full ${
                infoOpen ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
              }`}
            >
              {recording && <span className="size-2 rounded-full bg-red-500 anim-pulse-rec shrink-0" />}
              <span className="inline-flex items-center gap-1.5 min-w-0 max-w-[16rem]">
                <span className="tabular-nums shrink-0 inline-block min-w-[5ch] text-start">{duration}</span>
                <span className="shrink-0">·</span>
                <span className="truncate">{roomName}</span>
              </span>
              <ChevronUp className={`size-[13px] shrink-0 transition-transform ${!infoOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        )}

        {/* One popover for every tier (GH-1768) — anchored to this wrapper. */}
        <AnimatePresence>
          {infoOpen && (
            <MeetingInfoPopover
              roomName={roomName}
              meetingCode={meetingCode}
              meetingCodeLoading={meetingCodeLoading}
              shareLink={shareLink}
              codeCopied={codeCopied}
              linkCopied={linkCopied}
              onCopyCode={() => void handleCopyMeetingCode()}
              onCopyLink={() => void handleCopyShareLink()}
            />
          )}
        </AnimatePresence>
        </div>

        {/* Center controls. Mobile lets this flex so buttons sit between
            the timer and hangup instead of overlapping them. */}
        <div
          data-call-bar-controls=""
          className={`flex items-center ${isMobile ? "flex-1 min-w-0 justify-center gap-1.5" : "gap-3 shrink-0"}`}
        >
          <InCallMicButton
            micOn={micOn}
            onToggleMic={onToggleMic}
            devices={callDevices}
            audio={audioOutput}
            openUp
            light
            small={isMobile}
            sheet={isMobile}
          />
          <InCallCameraButton
            cameraOn={cameraOn}
            onToggleCamera={onToggleCamera}
            devices={callDevices}
            openUp
            light
            small={isMobile}
          />
          {/* Hidden until a recording backend exists (RECORDING_ENABLED). */}
          {RECORDING_ENABLED && (
            <BarButton
              tone={recording ? "danger" : "light"}
              small={isMobile}
              onClick={onToggleRecording}
              label={recording ? t("call.controls.stopRecording") : t("call.controls.recordMeeting")}
              ariaPressed={recording}
              icon={
                recording ? (
                  <Square className="size-4" />
                ) : (
                  <Circle className="size-5 text-red-500" fill="#FB2C36" />
                )
              }
            />
          )}
          {showScreenInline && (
            <BarButton
              tone={screenSharing ? "active" : "light"}
              onClick={onToggleScreenShare}
              disabled={screenShareDisabled || screenShareNotSupported || screenShareGuestBlocked}
              label={
                screenShareNotSupported
                  ? t("call.controls.screenShareMobile")
                  : screenShareGuestBlocked
                    ? t("call.controls.screenShareGuestBlocked")
                    : screenShareDisabled
                      ? t("call.controls.screenShareBusy")
                      : screenSharing
                        ? t("call.controls.stopScreenShare")
                        : t("call.controls.shareScreen")
              }
              icon={screenSharing ? <MonitorOff className="size-5" /> : <MonitorUp className="size-5" />}
            />
          )}

          {showChatInline && (
            isMobile ? (
              <BarButton
                tone={activePanel === "chat" ? "active" : "light"}
                small
                onClick={() => togglePanel("chat")}
                label={
                  chatUnreadCount > 0
                    ? t("call.controls.toggleChatUnread", { count: chatUnreadCount })
                    : t("call.controls.chat")
                }
                ariaPressed={activePanel === "chat"}
                badge={chatUnreadCount > 0 ? chatUnreadCount : undefined}
                icon={<MessageSquare className="size-5" />}
              />
            ) : (
              <BarButton
                tone={activePanel === "chat" ? "active" : "light"}
                onClick={() => togglePanel("chat")}
                ariaPressed={activePanel === "chat"}
                label={
                  chatUnreadCount > 0
                    ? t("call.controls.toggleChatUnread", { count: chatUnreadCount })
                    : t("call.controls.toggleChat")
                }
                badge={chatUnreadCount > 0 ? chatUnreadCount : undefined}
                icon={<MessageSquare className="size-5" />}
              />
            )
          )}

          {showReactionsInline && (
            <div ref={reactionsWrapRef} className="relative">
              <BarButton
                tone={reactionsOpen ? "active" : "light"}
                onClick={() => setReactionsOpen((open) => !open)}
                label={t("call.controls.openReactions")}
                ariaPressed={reactionsOpen}
                icon={<Smile className="size-5" />}
              />
              <AnimatePresence>
                {reactionsOpen && (
                  <motion.div
                    key="reactions-popover"
                    initial={{ opacity: 0, scale: 0.9, y: 4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 4 }}
                    transition={{ type: "spring", damping: 26, stiffness: 280 }}
                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex items-center gap-1 p-2 z-50 bg-background border border-border rounded-2xl shadow-2xl"
                    role="menu"
                    aria-label={t("call.controls.reactionsAriaLabel")}
                  >
                    {REACTION_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        role="menuitem"
                        onClick={() => handlePickReaction(emoji)}
                        aria-label={t("call.controls.reactWith", { emoji })}
                        className="size-10 rounded-xl hover:bg-muted text-2xl flex items-center justify-center transition-colors hover:scale-110"
                      >
                        {emoji}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {showHandRaiseInline && (
            <BarButton
              tone={handRaised ? "active" : "light"}
              onClick={onToggleHandRaised}
              label={handRaised ? t("call.controls.lowerHand") : t("call.controls.raiseHand")}
              ariaPressed={handRaised}
              icon={<Hand className={`size-5 ${handRaised ? "anim-wave" : ""}`} />}
            />
          )}

          {isMobile && (
            <MeetingControlsOverflow
              tier="mobile"
              activePanel={activePanel}
              onPanelChange={onPanelChange}
              chatUnreadCount={chatUnreadCount}
              participantBadge={participantCount}
              screenSharing={screenSharing}
              screenShareDisabled={screenShareDisabled || screenShareGuestBlocked}
              screenShareNotSupported={screenShareNotSupported}
              onToggleScreenShare={onToggleScreenShare}
              reactionEmojis={REACTION_EMOJIS}
              onEmitReaction={onEmitReaction}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              handRaised={handRaised}
              onToggleHandRaised={onToggleHandRaised}
              isHost={isHost}
              recording={recording}
              onToggleRecording={onToggleRecording}
              onOpenSettings={() => setInviteOpen(true)}
            />
          )}

          {isMobile && (
            <button
              type="button"
              onClick={onLeave}
              title={t("call.controls.leaveCall")}
              aria-label={t("call.controls.leaveCall")}
              className="size-11 shrink-0 rounded-2xl bg-red-500 text-white flex items-center justify-center transition-colors hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
            >
              <HangUpIcon className="size-5" />
            </button>
          )}

          {!isMobile && (
            <button
              type="button"
              onClick={onLeave}
              title={t("call.controls.leaveCall")}
              aria-label={t("call.controls.leaveCall")}
              className="size-12 rounded-2xl bg-red-500 text-white flex items-center justify-center transition-colors hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
            >
              <HangUpIcon className="size-5" />
            </button>
          )}
        </div>

        {/* Right block — layouts / settings (superset extras) + people. */}
        <div className={`flex items-center justify-end gap-1 ${isMobile ? "shrink-0" : "flex-1 min-w-0"}`}>
          {/* Pop out the call into a floating window. open() is called straight
              from the click so the browser still sees the user gesture. */}
          {showLayoutsInline && pip && pip.tier !== "none" && (
            <BarButton
              tone={pip.isOpen ? "active" : "light"}
              onClick={() => (pip.isOpen ? pip.close() : void pip.open())}
              label={t("call.pip.open")}
              ariaPressed={pip.isOpen}
              icon={<PictureInPicture className="size-5" />}
            />
          )}

          {showLayoutsInline && (
            <div ref={layoutsWrapRef} className="relative">
              <button
                type="button"
                onClick={() => setLayoutsOpen((open) => !open)}
                aria-expanded={layoutsOpen}
                aria-label={t("call.controls.changeLayout")}
                title={t("call.controls.changeLayout")}
                className={`flex items-center gap-2 px-3 h-10 rounded-xl transition-colors ${
                  layoutsOpen
                    ? "bg-foreground/10 text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                }`}
              >
                <LayoutModeIcon mode={viewMode} className="size-[18px]" />
                <ChevronUp className={`size-3.5 transition-transform ${!layoutsOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {layoutsOpen && (
                  <motion.div
                    key="layouts-popover"
                    initial={{ opacity: 0, scale: 0.95, y: 4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 4 }}
                    transition={{ type: "spring", damping: 26, stiffness: 280 }}
                    className="absolute bottom-full mb-2 end-0 w-64 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden z-50 p-2"
                    role="menu"
                    aria-label={t("call.controls.changeLayout")}
                  >
                    <div className="px-3 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      {t("call.controls.layout")}
                    </div>
                    {LAYOUT_OPTIONS.map(({ mode, icon: OptionIcon, label, desc }) => (
                      <button
                        key={mode}
                        type="button"
                        role="menuitemradio"
                        aria-checked={viewMode === mode}
                        onClick={() => handlePickLayout(mode)}
                        className="w-full flex items-center gap-3 px-3 py-3 text-sm text-start transition-colors hover:bg-muted rounded-xl"
                      >
                        <OptionIcon className={`size-[17px] ${viewMode === mode ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="flex-1 min-w-0">
                          <span className={`block truncate ${viewMode === mode ? "font-bold text-foreground" : "font-semibold text-foreground/90"}`}>
                            {t(label)}
                          </span>
                          <span className="block text-[11px] text-muted-foreground truncate">{t(desc)}</span>
                        </span>
                        {viewMode === mode && <Check className="size-[15px] text-primary shrink-0" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Room settings — the design's order is Layout → Settings → People
              (the speaker now lives in the mic menu, GH-1859). */}
          {showInviteInline && isHost && (
            <div ref={inviteWrapRef} className="relative">
              <button
                type="button"
                onClick={() => setInviteOpen((o) => !o)}
                title={t("call.controls.roomSettings")}
                aria-label={t("call.controls.roomSettings")}
                aria-expanded={inviteOpen}
                className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
                  inviteOpen ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                }`}
              >
                <Settings className="size-[18px]" />
              </button>
              <AnimatePresence>
                {inviteOpen && (
                  <motion.div
                    key="invite-popover"
                    initial={{ opacity: 0, scale: 0.95, y: 4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 4 }}
                    transition={{ type: "spring", damping: 26, stiffness: 280 }}
                    className="absolute bottom-full mb-2 end-0 w-80 bg-background border border-border rounded-2xl shadow-2xl overflow-hidden z-50"
                    role="dialog"
                    aria-label={t("call.controls.roomSettings")}
                  >
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                      <Settings className="size-4 text-foreground" />
                      <span className="text-foreground text-xs font-black uppercase tracking-widest">
                        {t("call.controls.roomSettings")}
                      </span>
                    </div>
                    <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto no-scrollbar">{inviteContent}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {onSendMochi && showPeopleInline && (
            <BarButton
              tone="light"
              onClick={onSendMochi}
              label={t("call.controls.sendMochi")}
              icon={<Bot className="size-5" />}
            />
          )}

          {/* Share — opens the "Invite people" card. Icon-only, same
              resting/hover/open treatment as the Settings gear. */}
          {showShareInline && (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              title={t("call.share.button")}
              aria-label={t("call.share.button")}
              aria-haspopup="dialog"
              aria-expanded={shareOpen}
              className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
                shareOpen ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
              }`}
            >
              <Share2 className="size-[18px]" />
            </button>
          )}

          {showPeopleInline &&
            (isDesktop ? (
              // The design's People view is a full-height right-side slide-in
              // panel (same top-0 right-0 bottom-20 treatment as chat), not a
              // small anchored dropdown — so this routes through the shared
              // activePanel/SidePanel mechanism instead of its own popover.
              <button
                type="button"
                onClick={() => togglePanel("users")}
                title={t("call.controls.people")}
                aria-label={t("call.controls.people")}
                aria-expanded={activePanel === "users"}
                className={`flex items-center gap-2 px-3 h-10 rounded-xl transition-colors ${
                  activePanel === "users"
                    ? "bg-foreground/10 text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                }`}
              >
                <Users className="size-[18px]" />
                <span className="text-sm font-bold">{participantCount}</span>
                {waitingCount > 0 && (
                  <span className="ml-0.5 size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-black flex items-center justify-center">
                    {waitingCount}
                  </span>
                )}
              </button>
            ) : (
              <BarButton
                tone={activePanel === "users" ? "active" : "light"}
                onClick={() => togglePanel("users")}
                ariaPressed={activePanel === "users"}
                label={t("call.controls.toggleParticipants")}
                badge={participantCount}
                icon={<Users className="size-5" />}
              />
            ))}

          {showMore && !isMobile && (
            <MeetingControlsOverflow
              tier="compact"
              activePanel={activePanel}
              onPanelChange={onPanelChange}
              chatUnreadCount={chatUnreadCount}
              participantBadge={participantCount}
              screenSharing={screenSharing}
              screenShareDisabled={screenShareDisabled || screenShareGuestBlocked}
              screenShareNotSupported={screenShareNotSupported}
              onToggleScreenShare={onToggleScreenShare}
              reactionEmojis={REACTION_EMOJIS}
              onEmitReaction={onEmitReaction}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              handRaised={handRaised}
              onToggleHandRaised={onToggleHandRaised}
            />
          )}
        </div>

        {ringStatus && (
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
            role="status"
            aria-live="polite"
          >
            <div className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-medium shadow-[0_8px_24px_rgba(0,0,0,0.28)] whitespace-nowrap">
              {ringStatus.kind === "accepted"
                ? t("call.ringIn.statusAccepted", { name: ringStatus.name })
                : t("call.ringIn.statusRinging", { name: ringStatus.name })}
            </div>
          </div>
        )}
      </div>

      {shareOpen && roomId && (
        <InCallShareDialog
          roomId={roomId}
          shareLink={shareLink}
          meetingCode={meetingCode}
          meetingCodeLoading={meetingCodeLoading}
          onClose={() => setShareOpen(false)}
        />
      )}

      {isMobile && inviteOpen && (
        <div className="fixed inset-0 z-40 flex items-end justify-center px-3 pb-24">
          <button type="button" className="absolute inset-0 bg-foreground/20" aria-label={t("call.controls.sheetClose")} onClick={() => setInviteOpen(false)} />
          <div
            ref={inviteWrapRef}
            className="relative w-full max-w-sm bg-background border border-border rounded-3xl shadow-2xl overflow-hidden z-10"
            role="dialog"
            aria-label={t("call.controls.roomSettings")}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <Settings className="size-4 text-foreground" />
              <span className="text-foreground text-xs font-black uppercase tracking-widest">
                {t("call.controls.roomSettings")}
              </span>
            </div>
            <div className="p-4 space-y-4 max-h-[50vh] overflow-y-auto no-scrollbar">{inviteContent}</div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MeetingInfoPopoverProps {
  roomName: string;
  meetingCode?: string | null;
  meetingCodeLoading: boolean;
  shareLink: string;
  codeCopied: boolean;
  linkCopied: boolean;
  onCopyCode: () => void;
  onCopyLink: () => void;
}

/** The meeting-info panel — one component for every tier (GH-1768), so the
 *  phone, tablet and desktop triggers can never drift apart again. Anchored
 *  `start-0` above the bar's meta wrapper; the width cap keeps it on screen
 *  at phone widths. */
function MeetingInfoPopover({
  roomName,
  meetingCode,
  meetingCodeLoading,
  shareLink,
  codeCopied,
  linkCopied,
  onCopyCode,
  onCopyLink,
}: MeetingInfoPopoverProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="info-popover"
      initial={{ opacity: 0, scale: 0.95, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 4 }}
      transition={{ type: "spring", damping: 26, stiffness: 280 }}
      className="absolute bottom-full mb-2 start-0 w-[min(20rem,calc(100vw-2rem))] bg-background border border-border rounded-2xl shadow-2xl overflow-hidden z-50"
      role="dialog"
      aria-label={t("call.controls.meetingInfo")}
    >
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">
          {t("call.controls.meetingInfo")}
        </p>
        <p className="font-bold text-base truncate text-foreground">{roomName}</p>
      </div>
      <div className="p-4 space-y-3">
        {(meetingCode || meetingCodeLoading) && (
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
              {t("call.controls.meetingCode")}
            </p>
            <div className="flex items-center gap-2 px-3 py-3 bg-muted/40 border border-border rounded-xl">
              <Hash className="size-3.5 text-muted-foreground shrink-0" />
              {meetingCode ? (
                <>
                  <span className="flex-1 text-sm font-mono font-semibold tracking-wider text-foreground">
                    {meetingCode}
                  </span>
                  <button
                    type="button"
                    onClick={onCopyCode}
                    className="text-xs font-bold text-primary hover:underline shrink-0"
                  >
                    {codeCopied ? t("call.controls.copied") : t("call.controls.copyCode")}
                  </button>
                </>
              ) : (
                <span
                  role="status"
                  aria-label={t("call.controls.loadingCode")}
                  data-testid="meeting-code-loading"
                  className="flex-1 h-4 rounded bg-muted animate-pulse"
                />
              )}
            </div>
          </div>
        )}
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">
            {t("call.controls.shareLink")}
          </p>
          <div className="flex items-center gap-2 px-3 py-3 bg-muted/40 border border-border rounded-xl">
            <Link2 className="size-3.5 text-muted-foreground shrink-0" />
            <span dir="ltr" className="flex-1 min-w-0 text-sm font-medium truncate text-muted-foreground">
              {shareLink}
            </span>
            <button
              type="button"
              onClick={onCopyLink}
              className="text-xs font-bold text-primary hover:underline shrink-0"
            >
              {linkCopied ? t("call.controls.copied") : t("call.controls.copyLink")}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onCopyLink}
          className="w-full h-10 bg-primary text-primary-foreground font-bold rounded-2xl text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
        >
          {linkCopied ? <Check className="size-[15px]" /> : <Share2 className="size-[15px]" />}
          {linkCopied ? t("call.controls.copied") : t("call.controls.shareInviteLink")}
        </button>
      </div>
    </motion.div>
  );
}

/** Shared "close on outside click" behavior for the popovers above. */
function useClickOutside(open: boolean, ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (ref.current && target && !ref.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

type BarButtonTone = "light" | "danger" | "active";

interface BarButtonProps {
  tone: BarButtonTone;
  /** 44px — the design's phone bar (`.ic-bar .w-12` → 2.75rem). */
  small?: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  ariaPressed?: boolean;
  disabled?: boolean;
  /** Optional numeric badge rendered top-right (used for participant/chat counts). */
  badge?: number;
}

/**
 * In-call bar button — the design's `w-12 h-12 rounded-2xl` control (44px on
 * phones), the same shape as the mic/camera DeviceSelectButtons beside it.
 */
function BarButton({ tone, small, onClick, label, icon, ariaPressed, disabled, badge }: BarButtonProps) {
  const toneClass =
    tone === "danger"
      ? "bg-red-500 text-white hover:bg-red-600"
      : tone === "active"
        ? "bg-primary text-primary-foreground border border-primary"
        : "bg-foreground/[0.06] border border-border text-foreground hover:bg-foreground/10";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={ariaPressed ?? tone !== "light"}
      className={`relative ${small ? "size-11" : "size-12"} shrink-0 rounded-2xl flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${toneClass}`}
    >
      {icon}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-primary text-primary-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

